import React, { useMemo, useState } from 'react';
import { usePublicRegistrationFlow } from '../../../contexts/PublicRegistrationFlowContext';
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Checkbox,
  Chip,
  Paper
} from '@mui/material';
import { BRAND } from '../../../config/brand';
import { useTranslation } from 'react-i18next';

interface AddOn {
  id: string;
  label: string;
  description: string;
  yearlyPrice: number;
  monthlyPrice: number;
}

const addOnPrices = [
  { id: 'sube', yearlyPrice: 0, monthlyPrice: 0 },
  { id: 'emuhasebe', yearlyPrice: 0, monthlyPrice: 0 },
  { id: 'qrmenu', yearlyPrice: 1490, monthlyPrice: 150 },
  { id: 'ecommerce', yearlyPrice: 2500, monthlyPrice: 270 },
  { id: 'yemeksepeti', yearlyPrice: 600, monthlyPrice: 130 },
  { id: 'getir', yearlyPrice: 600, monthlyPrice: 130 },
  { id: 'trendyol', yearlyPrice: 600, monthlyPrice: 130 },
  { id: 'migros', yearlyPrice: 600, monthlyPrice: 130 },
  { id: 'callerid', yearlyPrice: 600, monthlyPrice: 130 },
  { id: 'courier', yearlyPrice: 1500, monthlyPrice: 130 },
  { id: 'pos', yearlyPrice: 1500, monthlyPrice: 300 },
  { id: 'efatura', yearlyPrice: 2000, monthlyPrice: 300 },
  { id: 'tvmenu', yearlyPrice: 1500, monthlyPrice: 320 },
  { id: 'restoran', yearlyPrice: 2000, monthlyPrice: 300 },
  { id: 'kiosk', yearlyPrice: 4500, monthlyPrice: 750 },
];


// Yıllık sabit baz fiyat (aylık olarak gösterilir) + Aylık baz fiyat
const YEARLY_BASE = 375;   // /ay gösterilir (4500 / 12)
const YEARLY_TOTAL_BASE = 4500; // /yıl gösterilir
const MONTHLY_BASE = 499;  // /ay

interface PricingCardProps {
  isYearly: boolean;
  primaryColor: string;
  popular?: boolean;
  addOns: AddOn[];
}

const POPULAR_BANNER_HEIGHT = 44;

const PricingCard: React.FC<PricingCardProps> = ({ isYearly, primaryColor, popular, addOns }) => {
  const { openRoleSelection } = usePublicRegistrationFlow();
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  const toggle = (id: string) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const addOnTotal = addOns.reduce((sum, a) => {
    if (selected[a.id]) return sum + (isYearly ? a.yearlyPrice : a.monthlyPrice);
    return sum;
  }, 0);

  const baseMonthly = isYearly ? YEARLY_BASE : MONTHLY_BASE;
  const totalMonthly = isYearly ? baseMonthly + Math.round(addOnTotal / 12) : baseMonthly + addOnTotal;
  const totalYearly = isYearly ? YEARLY_TOTAL_BASE + addOnTotal : null;

  return (
    <Paper
      elevation={popular ? 6 : 1}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: `2px solid ${popular ? primaryColor : '#e8eaf0'}`,
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
      }}
    >
      {/* EN POPÜLER rozeti */}
      {/* Kart başlık/fiyat hizasını korumak için iki kartta da aynı yükseklikte alan ayırıyoruz */}
      <Box
        sx={{
          height: POPULAR_BANNER_HEIGHT,
          backgroundColor: popular ? primaryColor : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {popular && (
          <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '0.98rem', letterSpacing: 1.6, lineHeight: 1.2 }}>
            {t('interactivePricing.popularBadge')}
          </Typography>
        )}
      </Box>

      {/* Üst Renkli Alan - Fiyat Bölümü */}
      <Box sx={{
        backgroundColor: popular ? `${primaryColor}12` : '#fafbfc',
        px: 3,
        pt: 3,
        pb: 2.5,
        borderBottom: `1px solid ${popular ? `${primaryColor}25` : '#eee'}`,
      }}>
        <Typography sx={{
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: 2.5,
          color: primaryColor,
          textTransform: 'uppercase',
          mb: 1.5,
        }}>
          {isYearly ? t('interactivePricing.yearlyPackage') : t('interactivePricing.monthlyPackage')}
        </Typography>

        {/* Dinamik Fiyat - Büyük ve Bold */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
          <Typography sx={{ fontSize: '3.8rem', fontWeight: 900, color: '#111', lineHeight: 1, letterSpacing: -2 }}>
            {totalMonthly.toLocaleString('tr-TR')}
          </Typography>
          <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color: '#111', mb: 0.2 }}>₺</Typography>
          <Typography sx={{ fontSize: '1rem', color: '#888', ml: 0.5 }}>{t('interactivePricing.perMonth')}</Typography>
        </Box>

        <Typography sx={{ fontSize: '0.85rem', color: '#666', mb: 0.5 }}>
          {t('interactivePricing.totalLabel')}: <strong style={{ color: '#444' }}>{totalYearly ? `${totalYearly.toLocaleString('tr-TR')} ₺/yıl` : `${(totalMonthly * 12).toLocaleString('tr-TR')} ₺/yıl`}</strong>
        </Typography>
        {isYearly ? (
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: primaryColor }}>
            {t('interactivePricing.yearlySavings')}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: '0.85rem', color: '#52b788' }}>
            {t('interactivePricing.flexiblePayment')}
          </Typography>
        )}
      </Box>

      {/* Alt Alan - Paket ve Ek Özellikler */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1, flex: 1 }}>
        {/* Standart Paket Kutusu */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: `2px solid ${primaryColor}`,
          borderRadius: 2,
          p: 1.5,
          mb: 2.5,
          backgroundColor: `${primaryColor}0D`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{
              width: 30, height: 30, borderRadius: 1.5,
              backgroundColor: primaryColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: '1.1rem', flexShrink: 0,
            }}>✓</Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#111' }}>{t('interactivePricing.standardPackageTitle')}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: primaryColor, fontWeight: 500 }}>{t('interactivePricing.standardPackageSubtitle')}</Typography>
            </Box>
          </Box>
          <Typography sx={{ fontWeight: 800, color: primaryColor, fontSize: '1rem' }}>
            {isYearly ? `${YEARLY_TOTAL_BASE.toLocaleString('tr-TR')}₺` : `${MONTHLY_BASE}₺`}
          </Typography>
        </Box>

        {/* Ek Özellikler Başlık + Tümünü Seç */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
          <Typography sx={{ fontWeight: 700, color: '#111', fontSize: '0.9rem' }}>{t('interactivePricing.addOnsTitle')}</Typography>
          <Box
            onClick={() => {
              const allSelected = addOns.every(a => selected[a.id]);
              const next: Record<string, boolean> = {};
              addOns.forEach(a => { next[a.id] = !allSelected; });
              setSelected(next);
            }}
            sx={{
              fontSize: '0.76rem',
              fontWeight: 600,
              color: primaryColor,
              cursor: 'pointer',
              userSelect: 'none',
              px: 1.2,
              py: 0.4,
              borderRadius: 1.5,
              border: `1.5px solid ${primaryColor}40`,
              transition: 'all 0.15s ease',
              '&:hover': { backgroundColor: `${primaryColor}10` },
            }}
          >
            {addOns.every(a => selected[a.id]) ? t('interactivePricing.clearAll') : t('interactivePricing.selectAll')}
          </Box>
        </Box>
        <Typography sx={{ fontSize: '0.8rem', color: '#aaa', mb: 1.5 }}>{t('interactivePricing.addOnsSubtitle')}</Typography>

        {/* Checkbox Listesi - Kaydırılabilir */}
        <Box sx={{
          maxHeight: 300,
          overflowY: 'auto',
          pr: 0.5,
          '&::-webkit-scrollbar': { width: 8, borderRadius: 4 },
          '&::-webkit-scrollbar-track': { background: '#f0f0f0', borderRadius: 4 },
          '&::-webkit-scrollbar-thumb': { background: primaryColor, borderRadius: 4 },
        }}>
          {addOns.map(a => (
            <Box
              key={a.id}
              onClick={() => toggle(a.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: `1.5px solid ${selected[a.id] ? primaryColor : '#ededf0'}`,
                borderRadius: 2,
                p: 1.2,
                mb: 1,
                cursor: 'pointer',
                backgroundColor: selected[a.id] ? `${primaryColor}0A` : 'white',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: primaryColor, backgroundColor: `${primaryColor}06` }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={!!selected[a.id]}
                  onChange={() => toggle(a.id)}
                  onClick={e => e.stopPropagation()}
                  size="small"
                  sx={{ p: 0, color: '#d0d0d0', '&.Mui-checked': { color: primaryColor } }}
                />
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#222' }}>{a.label}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#bbb' }}>{a.description}</Typography>
                </Box>
              </Box>
              <Typography sx={{ fontWeight: 700, color: primaryColor, fontSize: '0.88rem', whiteSpace: 'nowrap', ml: 1 }}>
                {(isYearly ? a.yearlyPrice : a.monthlyPrice).toLocaleString('tr-TR')}₺
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Alt Buton */}
      <Box sx={{ px: 3, pb: 3, pt: 1.5 }}>
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={() => openRoleSelection()}
          sx={{
            backgroundColor: primaryColor,
            py: 1.7,
            fontWeight: 700,
            fontSize: '1rem',
            borderRadius: 2,
            boxShadow: `0 4px 16px ${primaryColor}40`,
            '&:hover': { opacity: 0.92, backgroundColor: primaryColor, boxShadow: `0 6px 20px ${primaryColor}55` }
          }}
        >
          {t('interactivePricing.ctaButton')}
        </Button>
      </Box>
    </Paper>
  );
};

const InteractivePricingSection: React.FC = () => {
  const primaryColor = BRAND.colors.primary;
  const { t } = useTranslation();
  const addOns: AddOn[] = useMemo(
    () =>
      addOnPrices.map((item) => ({
        ...item,
        label: t(`interactivePricing.addOns.${item.id}.label`),
        description: t(`interactivePricing.addOns.${item.id}.description`),
      })),
    [t]
  );

  return (
    <Box sx={{ py: 8, backgroundColor: '#f5f7fa' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          textAlign="center"
          sx={{ fontWeight: 700, color: primaryColor, mb: 1, fontSize: { xs: '2rem', md: '2.8rem' } }}
        >
          {t('interactivePricing.sectionTitle')}
        </Typography>
        <Typography textAlign="center" sx={{ color: '#666', mb: 5, fontSize: '1rem' }}>
          {t('interactivePricing.sectionSubtitle')}
        </Typography>

        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} sm={6}>
            <PricingCard isYearly={true} primaryColor={primaryColor} popular={true} addOns={addOns} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PricingCard isYearly={false} primaryColor={primaryColor} popular={false} addOns={addOns} />
          </Grid>
        </Grid>

        {/* Alt Bilgi */}
        <Box sx={{ mt: 4, textAlign: 'center', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
          {[
            t('interactivePricing.footer.badge1'),
            t('interactivePricing.footer.badge2'),
            t('interactivePricing.footer.badge3')
          ].map(text => (
            <Chip key={text} label={text} sx={{ backgroundColor: `${primaryColor}15`, color: primaryColor, fontWeight: 600 }} />
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default InteractivePricingSection;
